import { format, formatDistanceToNow } from "date-fns";
import { AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type {
  Candidate,
  CandidateFlags,
  LapsedRequirement,
} from "./candidate-views";

const STATUS_BADGE_CLASS_NAME = "border hover:bg-transparent";
const HEADER_META_CLASS_NAME =
  "text-[11px] font-medium uppercase tracking-wide text-slate-400";

// Rejection outranks withdrawal, which outranks hired, so a card never shows
// two competing statuses.
export function CandidateStatusBadge({ flags }: { flags: CandidateFlags }) {
  if (flags.isRejected) {
    return (
      <Badge
        className={cn(
          STATUS_BADGE_CLASS_NAME,
          "border-red-200 bg-red-50 text-red-700 hover:bg-red-50",
        )}
      >
        Rejected
      </Badge>
    );
  }
  if (flags.isWithdrawn) {
    return (
      <Badge
        className={cn(
          STATUS_BADGE_CLASS_NAME,
          "border-slate-200 bg-slate-100 text-slate-600 hover:bg-slate-100",
        )}
      >
        Withdrawn
      </Badge>
    );
  }
  if (flags.isHired) {
    return (
      <Badge
        className={cn(
          STATUS_BADGE_CLASS_NAME,
          "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-50",
        )}
      >
        Hired
      </Badge>
    );
  }
  return null;
}

export function AppliedLabel({
  interestedAt,
}: {
  interestedAt: Date | string | null;
}) {
  if (!interestedAt) {
    return null;
  }
  const date = new Date(interestedAt);
  return (
    <span title={format(date, "PPpp")} className={HEADER_META_CLASS_NAME}>
      Applied {formatDistanceToNow(date, { addSuffix: true })}
    </span>
  );
}

// Shares AppliedLabel's styling so the two lines read as one metadata stack.
// "Not messaged" is shown explicitly so outreach gaps are visible at a glance,
// and it matches the messaged filter: only the team's own messages since the
// candidate applied count, not AI follow-ups or older threads.
export function LastMessagedLabel({
  lastMessage,
  lastOrgMessageAt,
}: {
  lastMessage: Candidate["lastMessage"];
  lastOrgMessageAt: Candidate["lastOrgMessageAt"];
}) {
  if (!lastOrgMessageAt) {
    // Amber flags the outreach gap; matches the "Needs review" badge's tone and
    // stays readable at this small size, unlike lighter yellows.
    return (
      <span className={cn(HEADER_META_CLASS_NAME, "text-amber-700")}>
        Not messaged
      </span>
    );
  }
  // The newest message is never older than the team's, so a candidate's
  // newest message is a reply to it.
  const replied = lastMessage !== null && !lastMessage.fromOrganization;
  const date = new Date(replied ? lastMessage.createdAt : lastOrgMessageAt);
  const verb = replied ? "Replied" : "Messaged";
  return (
    <span title={format(date, "PPpp")} className={HEADER_META_CLASS_NAME}>
      {verb} {formatDistanceToNow(date, { addSuffix: true })}
    </span>
  );
}

export function NeedsReviewBadge({ count }: { count: number }) {
  return (
    <Badge
      variant="outline"
      className="max-w-full gap-1 whitespace-normal border-amber-200 bg-amber-50 text-xs text-amber-700"
      title="This candidate has credentials that may no longer meet the job requirements."
    >
      <AlertTriangle aria-hidden="true" size={10} />
      Needs review ({count})
    </Badge>
  );
}

export function LapsedRequirementBadges({
  lapsed,
}: {
  lapsed: LapsedRequirement[];
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <NeedsReviewBadge count={lapsed.length} />
      {lapsed.map((requirement) => (
        <Badge
          key={requirement.id}
          variant="outline"
          className="max-w-full whitespace-normal border-slate-200 bg-slate-50 text-xs text-slate-600"
        >
          {requirement.displayLabel}
        </Badge>
      ))}
    </div>
  );
}
